import { BOSSES } from './bosses.js';
import { ACT2_BOSSES, CAMPAIGN_BOSSES } from './act2Bosses.js';
import { ACT3_BOSSES } from './act3Bosses.js';
import { challengeLevelForBoss } from './challengeBosses.js';
import {
  ACT1_CLEAR_BONUS, ACT1_PAR_SECONDS,
  ACT2_CLEAR_BONUS, ACT2_PAR_SECONDS,
  ACT3_CLEAR_BONUS, ACT3_PAR_SECONDS,
} from '../systems/progression/Currency.js';

/**
 * The game's acts, as a starting point you can pick.
 *
 * ACT2-BIBLE.md §1 originally locked Act II as an **appended continuation**:
 * the Undersong bosses carried on the same run after the Unmade King fell, one
 * thirteen-boss campaign. Act select existed as the release valve for the fact
 * that the only way to see Act II was to clear Act I first, every single time —
 * and it was deliberately a **practice route** that paid nothing, because a
 * rewarded shortcut *through* the campaign would have been a strictly faster
 * way to earn than the run it skipped.
 *
 * Both halves of that are now retired. **Act I ends at the Unmade King**, and
 * Act II is a standalone six-boss act entered from this screen, exactly like
 * Act III (ACT3-BIBLE.md §1) already was. The chain of consequences:
 *
 *  - Act II is no longer a shortcut through anything, so the one argument for
 *    it paying nothing is gone. It is **rewarded in full** — its existing
 *    tables (ACT2_ECHO_BONUS / ACT2_SHARDS), its own board, its own Reprise
 *    run-worth. `practice` therefore describes no act at all any more; it stays
 *    as a per-act flag because Boss Challenge still needs the same contract and
 *    a future act may want it.
 *  - Each act now walks only its own slots, so each needs its **own leaderboard
 *    board**: the score's progress term assumes a run began at that act's
 *    start, and three acts sharing one ladder would rank none of them. The old
 *    `campaign` board keeps the thirteen-boss rows that were legitimately
 *    earned under the appended shape — it is legacy and read-only, because a
 *    seven-boss Act I clear can no longer reach those totals.
 *  - Each act needs its own clear bonus and par (systems/progression/Currency.js).
 *
 *  - **Act I** — seven bosses, the on-ramp. Always unlocked, the only act
 *    Endless loops, and the lowest-paying by a wide margin.
 *  - **Act II** — six bosses, unlocked by unmaking the King. Rewarded.
 *  - **Act III** — six bosses, unlocked by finishing the Undersong. Rewarded.
 */

/** Where Act III's slots begin in the full roster — the campaign's length. */
export const ACT3_START_INDEX = CAMPAIGN_BOSSES.length;

/**
 * Every boss slot a run can occupy, in roster order. Only `_bossDefForIndex`
 * reads it: each act walks from its own `startIndex` to its own `endIndex`
 * (see core/campaignFlow.js) and so can never wander into another act's slots.
 */
export const FULL_ROSTER = [...CAMPAIGN_BOSSES, ...ACT3_BOSSES];

export const ACTS = [
  {
    id: 'act1',
    name: 'ACT I — THE UNMADE GAUNTLET',
    blurb: 'Climb the dying court and unmake the King. Seven bosses, and the only act that starts open.',
    startIndex: 0,
    endIndex: BOSSES.length,
    practice: false,
    // Its own ladder, not the old thirteen-boss `campaign` board: an Act I clear
    // is seven bosses now and could never out-score the rows that board holds.
    board: 'act1',
    // Always available — nothing to clear ahead of it.
    requiresAct: null,
    scoring: { clearBonus: ACT1_CLEAR_BONUS, parSeconds: ACT1_PAR_SECONDS },
  },
  {
    id: 'act2',
    name: 'ACT II — THE UNDERSONG',
    blurb: 'Down past the floor the King was holding shut. Six bosses of the deep — rewards, leaderboard and all.',
    startIndex: BOSSES.length,
    endIndex: CAMPAIGN_BOSSES.length,
    practice: false,
    board: 'act2',
    // Clearing Act I is what opens the floor — the same beat that used to drop
    // the player through it mid-run.
    requiresAct: 'act1',
    scoring: { clearBonus: ACT2_CLEAR_BONUS, parSeconds: ACT2_PAR_SECONDS },
  },
  {
    id: 'act3',
    name: 'ACT III — THE ANTHEM',
    blurb: 'Your own song, gone feral. Six bosses that answer the silence you left behind.',
    startIndex: ACT3_START_INDEX,
    endIndex: FULL_ROSTER.length,
    practice: false,
    board: 'act3',
    requiresAct: 'act2',
    scoring: { clearBonus: ACT3_CLEAR_BONUS, parSeconds: ACT3_PAR_SECONDS },
  },
];

/** The act a run starting at `startIndex` belongs to; Act I for anything unrecognised. */
export function actForStartIndex(startIndex) {
  return ACTS.find((act) => act.startIndex === startIndex) ?? ACTS[0];
}

/**
 * Act I is always available. Every later act opens on **clearing** the act
 * before it — Act I for Act II, Act II for Act III.
 *
 * A clear, not a kill, and not a depth reached. Both of the weaker bars have
 * been tried here:
 *
 *  - `highestGauntletIndex >= startIndex` gated Act II back when it was a
 *    rewardless practice route you were allowed to fail your way into, and the
 *    descent advanced into exactly that index. No run descends now, so the
 *    index is unreachable and depth says nothing.
 *  - `defeatedBosses.includes(<the act's last boss>)` replaced it, and is the
 *    wrong bar for a different reason: it records a kill in ANY mode, and both
 *    the Unmade King and the Undersong are in CHALLENGE_ROSTER
 *    (data/challengeBosses.js). One Boss Challenge win — a single fight, on a
 *    catch-up build, paying nothing — would open a fully rewarded act.
 *
 * `clearedActs` (SaveManager.recordActCleared) is written only past _endRun's
 * rewardless early return, so it means what it says. These are spoiler gates as
 * much as difficulty ones: the act select screen names the act, and nothing
 * should name it before it's earned.
 */
export function isActUnlocked(act, statistics) {
  if (!act.requiresAct) return true;
  return Boolean(statistics?.clearedActs?.includes(act.requiresAct));
}

/**
 * Which act a CHALLENGE_ROSTER def belongs to, or null for the
 * challenge-exclusive bosses (which belong to no act and are never gated).
 */
export function actIdForBoss(bossDef) {
  if (ACT2_BOSSES.includes(bossDef)) return 'act2';
  if (ACT3_BOSSES.includes(bossDef)) return 'act3';
  if (BOSSES.includes(bossDef)) return 'act1';
  return null;
}

/**
 * The spoiler gate, applied to the roster-reading screens rather than only to
 * act select.
 *
 * ActSelect and LoreMenu both take the trouble to say nothing about an act the
 * player hasn't earned; Boss Challenge and Card Practice read CHALLENGE_ROSTER
 * straight through and used to name every Act II and Act III boss — portrait,
 * epithet-free name, "Act III · Finale" — to a save that had never cleared Act
 * I, and let it fight The Anthem. Same bar as isActUnlocked, because it is the
 * same gate: an act's contents stay unnamed until the act before it is cleared.
 *
 * Once an act is unlocked, every boss in it is available in Boss Challenge
 * immediately, including ones the player hasn't reached in the act itself.
 * That is deliberate and unchanged — the mode exists to drill a fight in
 * isolation, and an act you've unlocked is an act you've been told about.
 */
export function isBossLocked(bossDef, statistics) {
  const actId = actIdForBoss(bossDef);
  if (!actId) return false;
  const act = ACTS.find((a) => a.id === actId);
  return !!act && !isActUnlocked(act, statistics);
}

/**
 * The level a non-Act-I start is granted, so the build is in the same
 * neighbourhood as one a player would actually arrive with. Derived from the
 * act's opening boss rather than hardcoded, the same way challengeLevelForBoss
 * derives a Boss Challenge start — so a balance change to an act's HP staircase
 * can't leave this stranded at an old value.
 */
export function actStartLevel(act) {
  if (act.startIndex <= 0) return 1;
  const opener = act.id === 'act3' ? ACT3_BOSSES[0] : ACT2_BOSSES[0];
  // Two derived terms, no magic number:
  //  - what a single fight against this act's opener is worth on its own
  //    (challengeLevelForBoss, the curve Boss Challenge already uses), plus
  //  - one level per boss the start skipped, because the campaign guarantees
  //    exactly that — ProgressionSystem grants a level on every boss:died.
  // It lands a little under a real arrival (wave XP isn't counted), which is
  // the right side to err on: an act-select start is the ONLY way into Acts II
  // and III now, so this build is what their HP staircases are actually fought
  // with rather than a catch-up approximation of one. For Act III that was
  // already true and is why its staircase opens BELOW the Undersong
  // (ACT3-BIBLE §9); Act II's staircase was tuned against a real arrival off
  // the King and now meets a slightly leaner build, erring toward harder.
  return challengeLevelForBoss(opener) + act.startIndex;
}
